import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FileText, Link2 } from "lucide-react";
import { NumberField } from "@/features/admin/components";
import { TextField } from "@/shared/ui/core";
import {
  FormRow,
  FormSection,
  FormSectionStack,
  RepeatableList,
  TextAreaField,
} from "@/features/admin/components";
import { ProductImageField } from "../ProductImageField";
import { ProductThumb } from "../ProductThumb";

/** Una fila de la galería: la dirección y su vista previa al lado. */
function GalleryRow({ name }: { name: string }) {
  const { t } = useTranslation();
  const url = (useWatch({ name: `${name}.value` }) ?? "") as string;

  return (
    <div className="flex items-start gap-4">
      <ProductThumb url={url.trim() || undefined} name={t("Vista previa")} size="lg" />
      <TextField
        name={`${name}.value`}
        label={t("Dirección de la imagen")}
        icon={<Link2 />}
        inputMode="url"
        autoComplete="off"
        placeholder="https://… o /assets/products/…"
        fieldClassName="flex-1"
      />
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Paso 7 · Las imágenes del producto y su ficha técnica.
 *
 * Es el último paso de la lista de Anahí, y lleva las dos cosas que ella
 * nombró juntas: "las fotos y la ficha".
 *
 * ── Estuvo partido en dos, y se volvió a juntar ─────────────────────
 * "Fotos" y "Ficha técnica" fueron dos pasos durante unas horas porque
 * juntos medían 1207 px sobre una pantalla de 900. El corte tenía su
 * lógica —las imágenes salen de una carpeta y el texto se copia del
 * bote— pero era un paso que ella no pidió, y manda su lista. Vuelve a
 * pasarse de la pantalla, y está aceptado.
 *
 * ── Por dirección, no por subida ────────────────────────────────────
 * Todavía no hay subida de archivos, y fingir que la hay con un botón
 * que no funciona es peor que no tenerla. Se pega la dirección de un
 * archivo ya publicado: una completa de otro servidor (`https://…`) o
 * una ruta de este mismo sitio (`/assets/…`), que es la de lo que viaja
 * dentro del repositorio.
 *
 * ── La vista previa no es adorno ────────────────────────────────────
 * Una dirección escrita a mano se equivoca a menudo, y sin previa el
 * error se descubre en la tienda. Cuando la imagen no carga,
 * `ProductThumb` cae al hueco con icono, que es justo la señal de "esta
 * dirección no sirve".
 *
 * ── El orden de las tres tarjetas ───────────────────────────────────
 * La foto PRINCIPAL va aparte de la galería porque no es una más: es la
 * que sale en la tarjeta del catálogo y en el carrito. La GALERÍA va en
 * medio. Y la FICHA cierra, con sus cinco datos en un solo bloque:
 * presentación, posología, el PDF con su nombre y el aviso legal son la
 * misma idea —lo que está impreso en el envase— y quien los captura los
 * tiene todos delante a la vez.
 * ═══════════════════════════════════════════════════════════════════
 */
export function MediaStep() {
  const { t } = useTranslation();

  return (
    <FormSectionStack>
      <ProductImageField />

      <FormSection
        title={t("Galería")}
        description={t("Las demás fotos de la ficha, en el orden en que se pasan.")}
        columns={1}
        compact
      >
        <RepeatableList
          name="imageUrls"
          max={8}
          addLabel={t("Añadir foto")}
          itemLabel={(index) => t("Foto {{n}}", { n: index + 1 })}
          emptyHint={t("Sin galería, la ficha enseña solo la foto principal.")}
          newItem={() => ({ value: "" })}
        >
          {({ name }) => <GalleryRow name={name} />}
        </RepeatableList>
      </FormSection>

      <FormSection
        title={t("Ficha técnica")}
        /*
          Sin explicación. Decía "lo que está impreso en el bote, y el PDF
          descargable si lo hay", que es lo que ya dicen el título y las
          cuatro etiquetas de debajo —Presentación, Posología, PDF de la
          ficha, Aviso legal—. Son 30 px en un paso que ya se pasa de la
          pantalla, y no explicaban nada que no estuviera a la vista.
        */
        columns={3}
        compact
      >
        <TextField
          name="presentation"
          label={t("Presentación")}
          placeholder={t("Frasco de 60 cápsulas")}
        />
        <TextField
          name="dosage"
          label={t("Posología")}
          placeholder={t("Tomar 2 cápsulas al día con alimentos")}
        />
        <TextField
          name="datasheet.url"
          label={t("PDF de la ficha")}
          icon={<FileText />}
          inputMode="url"
          autoComplete="off"
          /*
            El hueco anuncia LAS DOS formas. Antes decía solo "https://…",
            y esa media verdad es lo que empuja a pegar una dirección de
            `localhost` para conseguir lo mismo — que se rompe en cuanto la
            ve otra persona. Ver `urlOrSitePathSchema`.

            (`productDatasheetSchema.url` era `z.url()` a secas mientras la
            foto ya aceptaba las dos formas: la MISMA dirección se
            guardaba como imagen y se rechazaba como PDF. Ahora las dos
            usan la misma regla.)
          */
          placeholder="https://… o /fichas/…"
        />
        <TextField
          name="datasheet.name"
          label={t("Nombre visible del PDF")}
          placeholder={t("Ficha técnica (PDF)")}
        />
        {/*
          El aviso legal es un párrafo copiado del bote —alérgenos, "no es
          medicamento"— y va a la fila entera: en un tercio de fila se
          escribe a través de una rendija de cuatro palabras.
        */}
        <FormRow>
          <TextAreaField
            name="legend"
            label={t("Aviso legal")}
            rows={2}
            hint={t("No es medicamento, alérgenos, avisos sanitarios.")}
          />
        </FormRow>
      </FormSection>

      {/*
        ── Datos de etiqueta (Anahí, 28-ago) ─────────────────────────
        Todo lo que producción guardaba del bote y aquí no tenía campo:
        SKU, formato, porción, uso sugerido, fabricante, certificaciones,
        ingredientes y contraindicaciones. La ficha pública lo enseña en
        "Datos del producto". Ver `productFactsSchema`.
      */}
      <FormSection title={t("Datos de etiqueta")} columns={3} compact>
        <TextField name="facts.sku" label={t("SKU")} placeholder="ITB-DTX-001" />
        <TextField name="facts.format" label={t("Formato")} placeholder={t("cápsulas")} />
        <NumberField
          name="facts.servingsPerContainer"
          label={t("Porciones por envase")}
          placeholder="60"
        />
        <TextField
          name="facts.servingSize"
          label={t("Tamaño de la porción")}
          placeholder={t("2 cápsulas (1,370 mg)")}
        />
        <TextField
          name="facts.manufacturedIn"
          label={t("Fabricado en")}
          placeholder="EEUU"
        />
        <TextField
          name="facts.manufacturedFor"
          label={t("Fabricado para")}
          placeholder={t("It's The Best — dirección")}
        />
        <FormRow>
          <TextAreaField
            name="facts.suggestedUse"
            label={t("Uso sugerido")}
            rows={2}
            hint={t("Como suplemento, tomar 2 cápsulas al día.")}
          />
        </FormRow>
        <FormRow>
          <RepeatableList
            name="facts.certifications"
            max={12}
            addLabel={t("Añadir certificación")}
            itemLabel={(index) => t("Certificación {{n}}", { n: index + 1 })}
            emptyHint={t("GMP, FDA registered, orgánico…")}
            newItem={() => ({ value: "" })}
          >
            {({ name }) => <TextField name={`${name}.value`} label={t("Certificación")} />}
          </RepeatableList>
        </FormRow>
        <FormRow>
          <RepeatableList
            name="facts.ingredients"
            max={40}
            addLabel={t("Añadir ingrediente")}
            itemLabel={(index) => t("Ingrediente {{n}}", { n: index + 1 })}
            emptyHint={t("La lista de ingredientes tal como viene en la etiqueta.")}
            newItem={() => ({ value: "" })}
          >
            {({ name }) => (
              <TextAreaField name={`${name}.value`} label={t("Ingrediente")} rows={2} />
            )}
          </RepeatableList>
        </FormRow>
        <FormRow>
          <RepeatableList
            name="facts.contraindications"
            max={20}
            addLabel={t("Añadir contraindicación")}
            itemLabel={(index) => t("Contraindicación {{n}}", { n: index + 1 })}
            emptyHint={t("Embarazo, lactancia, menores de edad, interacciones…")}
            newItem={() => ({ value: "" })}
          >
            {({ name }) => (
              <TextAreaField name={`${name}.value`} label={t("Contraindicación")} rows={2} />
            )}
          </RepeatableList>
        </FormRow>
      </FormSection>
    </FormSectionStack>
  );
}
